/* DealForecastTrajectoryPanel.jsx — Deal Room forecast trajectory mount.
 *
 * Renders:
 *  • confidence trajectory sparkline (last N weeks)
 *  • projected exchange + completion windows
 *  • predicted blockers (likely_causes from drift predictor)
 *  • operational momentum + recovery trend
 *
 * Auto-hides when no signals are available.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Gauge, TrendingUp, TrendingDown, AlertOctagon,
  GitBranch, Sparkles, Clock,
} from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 60_000;

const STABILITY_TONE = {
  deteriorating: { bg: "rgba(239,68,68,0.10)",  fg: "#9F1239", dot: "#E11D48" },
  volatile:      { bg: "rgba(245,158,11,0.10)", fg: "#92400E", dot: "#F59E0B" },
  steady:        { bg: "rgba(148,163,184,0.10)", fg: "#475569", dot: "#94A3B8" },
  stabilising:   { bg: "rgba(16,185,129,0.10)", fg: "#047857", dot: "#10B981" },
};

const Sparkline = ({ points }) => {
  if (!points || points.length < 2) return null;
  const width = 160;
  const height = 36;
  const scores = points.map((p) => p.score);
  const max = Math.max(...scores, 100);
  const min = Math.min(...scores, 0);
  const range = Math.max(1, max - min);
  const stepX = width / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * stepX} ${height - ((p.score - min) / range) * height}`)
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         className="block"
         data-testid="deal-forecast-sparkline">
      <path d={d} fill="none" stroke="#7C3AED" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
};

export function DealForecastTrajectoryPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/forecasting/transactions/${dealId}`);
      setData(res.data?.forecast || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);

  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refetch(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  if (loading || !data) return null;
  const ct = data.confidence_trajectory || {};
  const stab = data.operational_stability || {};
  const drift = data.operational_drift;
  const rl = data.recovery_likelihood;
  const ex = data.exchange_forecast;
  const co = data.completion_forecast;
  // Hide when there's no useful telemetry at all.
  if (!ct.latest && !drift && !ex && !co) return null;
  const tone = STABILITY_TONE[stab.band] || STABILITY_TONE.steady;
  const TrendIcon =
    ct.direction === "improving" ? TrendingUp :
    ct.direction === "weakening" ? TrendingDown : Gauge;

  return (
    <section
      className="gp-card overflow-hidden"
      data-testid="deal-forecast-trajectory-panel"
    >
      <header className="p-4 sm:p-5"
              style={{ borderBottom: "1px solid var(--gp-card-border)" }}>
        <div className="flex items-center gap-1.5">
          <Sparkles size={11} className="text-violet-600" strokeWidth={2.3} />
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-violet-700">
            Transaction Forecast
          </p>
        </div>
        <h3 className="text-[14px] font-semibold text-slate-900 mt-1 leading-tight">
          {stab.narrative}
        </h3>
        <div className="flex items-center flex-wrap gap-1.5 mt-2">
          <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-md"
                style={{ background: tone.bg, color: tone.fg }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
            {stab.band}
          </span>
          {ct.latest ? (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-md"
                  style={{ background: "rgba(99,102,241,0.08)", color: "#4338CA" }}>
              <TrendIcon size={9} strokeWidth={2.4} />
              {ct.direction} · {ct.delta_pts >= 0 ? "+" : ""}{ct.delta_pts ?? 0} pts
            </span>
          ) : null}
        </div>
      </header>

      <div className="p-4 sm:p-5 space-y-3">
        {ct.points && ct.points.length > 1 ? (
          <div className="rounded-lg p-3"
               style={{ background: "rgba(124,58,237,0.04)",
                        border: "1px solid rgba(124,58,237,0.10)" }}>
            <p className="text-[11px] text-slate-700 leading-snug mb-1.5">
              {ct.narrative}
            </p>
            <Sparkline points={ct.points} />
            <p className="text-[10.5px] text-slate-500 mt-1 tabular-nums">
              {ct.starting?.score}/100 → {ct.latest?.score}/100 · volatility {ct.volatility}/100
            </p>
          </div>
        ) : null}

        {(ex || co) ? (
          <div className="flex flex-wrap gap-2"
               data-testid="deal-forecast-windows">
            {ex ? (
              <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                    style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
                Exchange · {ex.band} · {ex.score}/100
              </span>
            ) : null}
            {co ? (
              <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                    style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
                Completion · {co.band} · {co.score}/100
              </span>
            ) : null}
          </div>
        ) : null}

        {drift && drift.likely_causes?.length ? (
          <div data-testid="deal-forecast-predicted-blockers">
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-violet-700 mb-1">
              Predicted operational pressure
            </p>
            <p className="text-[11.5px] text-slate-700 leading-snug">{drift.narrative}</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {drift.likely_causes.slice(0, 3).map((c, idx) => (
                <span key={idx}
                      className="text-[10.5px] px-2 py-0.5 rounded-md inline-flex items-center gap-1"
                      style={{ background: "rgba(245,158,11,0.08)",
                               color: "#92400E",
                               border: "1px solid rgba(245,158,11,0.20)" }}>
                  <AlertOctagon size={9} strokeWidth={2.3} />
                  {c.label}{c.evidence_days != null ? ` · ${c.evidence_days}d` : ""}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {rl ? (
          <div className="rounded-lg p-3"
               style={{ background: "rgba(16,185,129,0.04)",
                        border: "1px solid rgba(16,185,129,0.18)" }}
               data-testid="deal-forecast-recovery-trend">
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-emerald-800 mb-1">
              <GitBranch size={9} className="inline -mt-0.5 mr-1" strokeWidth={2.3} />
              Recovery trend
            </p>
            <p className="text-[11.5px] text-slate-800 leading-snug">{rl.narrative}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default DealForecastTrajectoryPanel;
